/**
 * Service layer — Milestone Documents
 *
 * Two document sources are merged into a unified list:
 *
 * 1. Legacy (Airtable) — records in the Documents table (tbl8KQZY9Hbu7ob3Y)
 *    linked to the milestone via the Project Deliverables field. Flagged with
 *    _legacy: true; not editable, but removable (deleteLegacyDocument) even
 *    after the milestone posts — these predate the app's posting workflow
 *    entirely, so the post-lock (which protects app-managed content) doesn't
 *    apply to them. Removal deletes the underlying Airtable record.
 *
 * 2. New (Supabase) — records in the milestone_documents table. Supports
 *    both file uploads (Supabase Storage) and external URL links.
 *
 * getMilestoneDocuments() fetches both and returns a single merged array,
 * legacy docs first, then Supabase docs sorted oldest-first.
 */

import { apiGet, apiDelete } from './_api.js'

const TBL_DOCS = 'tbl8KQZY9Hbu7ob3Y'   // Airtable Documents table

function docsApi(action, params, clerkToken) {
  const headers = { 'Content-Type': 'application/json' }
  if (clerkToken) headers['Authorization'] = `Bearer ${clerkToken}`
  return fetch('/api/documents', {
    method: 'POST',
    headers,
    body: JSON.stringify({ action, ...params }),
  }).then(r => r.json())
}

/**
 * Fetch legacy documents from Airtable Documents table for a milestone.
 * Returns a normalised array compatible with Supabase document shape,
 * with _legacy: true so the UI can render them as read-only.
 * @param {string} milestoneId — Airtable Project Deliverable record ID
 */
async function getLegacyDocuments(milestoneId) {
  try {
    // Must use the lookup field {Record ID (from Project Deliverables)}, NOT {Project Deliverables}.
    // {Project Deliverables} in a filterByFormula returns display names, not record IDs.
    const formula = 'FIND("' + milestoneId + '",ARRAYJOIN({Record ID (from Project Deliverables)},","))'
    const records = await apiGet(TBL_DOCS, 'filterByFormula=' + encodeURIComponent(formula) + '&maxRecords=100')

    return records.map(r => {
      const f = r.fields
      // URL priority: Document Link (URL) → Google Drive Document URL → Google Drive URL → File attachment
      const url = (f['Document Link (URL)'] && f['Document Link (URL)'].trim())
        || (f['Google Drive Document URL'] && f['Google Drive Document URL'].trim())
        || (f['Google Drive URL'] && f['Google Drive URL'].trim())
        || (Array.isArray(f['File']) && f['File'][0]?.url)
        || null

      if (!url) return null   // no usable URL — skip

      const rawType = (f['Type'] || '').trim()

      return {
        id:          r.id,
        milestone_id: milestoneId,
        name:        (f['Name'] || '').trim() || 'Document',
        url,
        storage_path: null,
        file_type:   rawType || 'link',
        source:      'link',
        created_by:  null,
        created_at:  f['Created'] || null,
        _legacy:     true,    // read-only; lives in Airtable, not Supabase
      }
    }).filter(Boolean)
  } catch (_) {
    return []
  }
}

/**
 * List all documents for a milestone — merges legacy Airtable docs with new Supabase docs.
 * Legacy docs appear first (as they were there first), then Supabase docs oldest-first.
 * @param {string} milestoneId — Airtable Project Deliverable record ID
 * @returns {Promise<{ documents: object[] }>}
 */
export async function getMilestoneDocuments(milestoneId) {
  const [legacyRes, supaRes] = await Promise.all([
    getLegacyDocuments(milestoneId),
    docsApi('list', { milestoneId }),
  ])

  const supaDocuments = supaRes.documents || []
  return { documents: [...legacyRes, ...supaDocuments] }
}

/**
 * Upload a file to Supabase Storage and save a document record.
 * Uses a signed URL so the browser uploads directly — avoids Vercel's 4.5MB body limit.
 * @param {Object} opts
 * @param {string} opts.milestoneId
 * @param {File}   opts.file — File object from an input element
 * @param {string} [opts.name] — optional display name; defaults to filename
 * @returns {Promise<object>} The created document record
 */
export async function uploadDocument({ milestoneId, file, name, source }) {
  const token = await window.__clerkGetToken()

  // 1. Get a signed upload URL from the server
  const urlRes = await docsApi('getUploadUrl', { milestoneId, filename: file.name }, token)
  if (urlRes.error) throw new Error(urlRes.error)

  // 2. PUT the file directly to Supabase Storage via the signed URL
  const uploadRes = await fetch(urlRes.signedUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'x-upsert':     'true',
    },
    body: file,
  })
  if (!uploadRes.ok) throw new Error(`Storage upload failed (${uploadRes.status})`)

  // 3. Confirm the upload and save the document record
  const fileType = file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : null
  const confirmRes = await docsApi('confirmUpload', {
    milestoneId,
    storagePath: urlRes.storagePath,
    publicUrl:   urlRes.publicUrl,
    name:        name || file.name,
    fileType,
    source,
  }, token)
  if (confirmRes.error) throw new Error(confirmRes.error)

  return confirmRes.document
}

/**
 * Save an external URL as a milestone document.
 * @param {Object} opts
 * @param {string} opts.milestoneId
 * @param {string} opts.url — external URL (Google Drive, SharePoint, etc.)
 * @param {string} [opts.name] — display label; defaults to the URL if omitted
 * @returns {Promise<object>} The created document record
 */
export async function addLink({ milestoneId, url, name }) {
  const token = await window.__clerkGetToken()
  const res = await docsApi('addLink', { milestoneId, url, name }, token)
  if (res.error) throw new Error(res.error)
  return res.document
}

/**
 * Delete a Supabase document (and its Storage file for uploads).
 * Only works for non-legacy documents — legacy Airtable docs are read-only.
 * @param {string} documentId — UUID from milestone_documents
 * @returns {Promise<{ ok: boolean }>}
 */
export async function deleteDocument(documentId) {
  const token = await window.__clerkGetToken()
  const res = await docsApi('delete', { documentId }, token)
  if (res.error) throw new Error(res.error)
  return res
}

/**
 * Delete a legacy document — the record lives in the Airtable Documents table (from the old
 * app), not Supabase, so it deletes through the Airtable proxy rather than /api/documents.
 * Used only for docs flagged `_legacy: true`; Supabase uploads use deleteDocument().
 * @param {string} recordId — Airtable Documents record ID
 * @returns {Promise<object>}
 */
export async function deleteLegacyDocument(recordId) {
  return apiDelete(TBL_DOCS, recordId)
}

/**
 * Update a document's display name and/or replace its stored file.
 *
 * File replacement uses the same signed-URL flow as uploadDocument:
 *   getUploadUrl → PUT to Storage → update (removes old file server-side).
 *
 * @param {Object} opts
 * @param {string} opts.documentId — UUID from milestone_documents
 * @param {string} [opts.name]     — new display name
 * @param {File}   [opts.newFile]  — replacement image file (omit to keep existing)
 * @param {string} [opts.milestoneId] — required when newFile is set (for storage path)
 * @returns {Promise<object>} The updated document record
 */
export async function updateDocument({ documentId, name, newFile, milestoneId }) {
  const token = await window.__clerkGetToken()

  let newStoragePath, newPublicUrl, newFileType
  if (newFile) {
    const urlRes = await docsApi('getUploadUrl', { milestoneId, filename: newFile.name }, token)
    if (urlRes.error) throw new Error(urlRes.error)

    const uploadRes = await fetch(urlRes.signedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': newFile.type || 'application/octet-stream', 'x-upsert': 'true' },
      body: newFile,
    })
    if (!uploadRes.ok) throw new Error(`Storage upload failed (${uploadRes.status})`)

    newStoragePath = urlRes.storagePath
    newPublicUrl   = urlRes.publicUrl
    newFileType    = newFile.name.includes('.') ? newFile.name.split('.').pop().toLowerCase() : null
  }

  const res = await docsApi('update', { documentId, name, newStoragePath, newPublicUrl, newFileType }, token)
  if (res.error) throw new Error(res.error)
  return res.document
}

/**
 * The Documents-list "Done" lock (docs/DOCUMENT_MARKUP.md). Marking a document Done blocks
 * replacing or deleting it until someone unlocks it; once the milestone posts, it can't be
 * unlocked without unposting (enforced client-side — see MilestoneSections.jsx's docsLocked,
 * the existing convention for this exact lock family).
 * @param {string} documentId — UUID from milestone_documents
 * @param {string} [actorName] — display name, stored for "locked by" display
 */
export async function markDocumentDone(documentId, actorName) {
  const token = await window.__clerkGetToken()
  const res = await docsApi('markDone', { documentId, actorName }, token)
  if (res.error) throw new Error(res.error)
  return res.document
}

/** Unlocks a document marked Done. @param {string} documentId — UUID from milestone_documents */
export async function unlockDocument(documentId) {
  const token = await window.__clerkGetToken()
  const res = await docsApi('unlock', { documentId }, token)
  if (res.error) throw new Error(res.error)
  return res.document
}

/**
 * Make the Design Review presentation (docs/DRB_PRESENTATION.md): a 16:9 slide PDF of the project's
 * metrics and its newest Discretionary Approvals drawings, saved as a NEW dated row in this
 * milestone's Documents (source 'drb-presentation'). Takes up to a minute, so a placeholder tab
 * opens right away (popup-blocker safe) and shows the PDF when it's ready.
 * @param {string} msId
 * @param {Array<{ sheetId, include, crop }>} [sheets] — the DRB window's choices, saved per sheet
 * @returns {Promise<{ document?: object, skipped?: string[], error?: string }>}
 */
export async function makeDrbPresentation(msId, sheets) {
  const tab = window.open('', '_blank')
  if (tab) {
    try {
      tab.document.write('<body style="margin:0;font-family:sans-serif;display:flex;align-items:center;justify-content:center;min-height:100vh;background:#07272D;color:#cfd8da;font-size:13px;text-align:center;padding:24px;box-sizing:border-box;">Making the DRB presentation from the latest drawings. This takes up to a minute.</body>')
      tab.document.close()
    } catch { /* best-effort */ }
  }
  try {
    const token = await window.__clerkGetToken()
    const res = await fetch('/api/drb-presentation', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify(sheets ? { msId, sheets } : { msId }),
    }).then(r => r.json().catch(() => ({ error: 'The presentation took too long or failed (HTTP ' + r.status + ').' })))
    if (res.error || !res.document) throw new Error(res.error || 'The presentation was not saved.')
    if (tab) tab.location = res.document.url
    else window.open(res.document.url, '_blank')
    return { document: res.document, skipped: res.skipped || [] }
  } catch (err) {
    if (tab) tab.close()
    return { error: err.message || String(err) }
  }
}

/**
 * The drawings the DRB presentation would use (the newest DAPC set) with each sheet's saved
 * choice — whether it goes in and how its slide is cropped (docs/DRB_PRESENTATION.md).
 * @param {string} msId
 * @returns {Promise<{ sheets?: Array<{ sheetId, number, title, pdfUrl, kind, defaultInclude, include, crop }>, error?: string }>}
 */
export async function getDrbPlan(msId) {
  try {
    const token = await window.__clerkGetToken()
    const res = await fetch('/api/drb-presentation', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify({ msId, action: 'plan' }),
    }).then(r => r.json().catch(() => ({ error: 'Could not read the drawings (HTTP ' + r.status + ').' })))
    if (res.error) return { error: res.error }
    return { sheets: res.sheets || [] }
  } catch (err) {
    return { error: err.message || String(err) }
  }
}
